import React, { useState } from 'react';

import { useMutation } from '@tanstack/react-query';
import { useHasPermission } from '@vlabs/permissions';
import { Button } from '@vlabs/uikit/components/controls/button/Button';
import { Radio } from '@vlabs/uikit/components/controls/radio/Radio';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import {
  deleteAllCardsFromList,
  detachAllCardsFromList,
} from './requests';

type TClearAction = 'delete' | 'detach';

interface ClearListsProps {
  listIds: string[];
  onCancel: VoidFunction;
  onClearLists?: VoidFunction;
}

export function ClearLists({
  listIds,
  onCancel,
  onClearLists = undefined,
}: ClearListsProps): React.JSX.Element {
  const { t } = useTranslation();
  const canDeleteCards = useHasPermission('card', 'delete');
  const canUpdateLists = useHasPermission('list', 'update');
  const [clearAction, setClearAction] = useState<TClearAction>(canDeleteCards ? 'delete' : 'detach');

  const clearListsMutation = useMutation({
    mutationFn: async ({ listIds: ids, clearAction: action }: { listIds: string[]; clearAction: TClearAction }) => {
      // eslint-disable-next-line no-restricted-syntax
      for (const id of ids) {
        if (action === 'delete') {
          // eslint-disable-next-line no-await-in-loop
          await deleteAllCardsFromList(id);
        } else {
          // eslint-disable-next-line no-await-in-loop
          await detachAllCardsFromList(id);
        }
      }
    },
    onSuccess: () => {
      toast.success(t('features:clear_lists.toast.success'));
      onClearLists?.();
      onCancel();
    },
    onError: () => {
      toast.error(t('features:clear_lists.toast.error'));
    },
  });

  const handleSubmit = (): void => {
    clearListsMutation.mutate({
      listIds,
      clearAction,
    });
  };

  return (
    <div>
      <header>
        <h5>{t('features:clear_lists.confirmation.title')}</h5>
        <span>
          {t('features:clear_lists.confirmation.subtitle')}
        </span>
      </header>

      <div>
        {!!canDeleteCards && (
          <Radio
            checked={clearAction === 'delete'}
            id="delete"
            label={t('features:clear_lists.confirmation.delete_content') as unknown as string}
            name="clearListAction"
            onChange={() => setClearAction('delete')}
          />
        )}

        {!!canUpdateLists && (
          <Radio
            checked={clearAction === 'detach'}
            id="detach"
            label={t('features:clear_lists.confirmation.detach_only') as unknown as string}
            name="clearListAction"
            onChange={() => setClearAction('detach')}
          />
        )}
      </div>

      <div>
        <Button
          disabled={clearListsMutation.isPending}
          isLoading={clearListsMutation.isPending}
          onClick={handleSubmit}
        >
          {t('features:clear_lists.btn.clear')}
        </Button>

        <Button
          kind="secondary"
          onClick={onCancel}
          variant="outlined"
        >
          {t('features:clear_lists.btn.cancel')}
        </Button>
      </div>
    </div>
  );
}
